/**
 * AgentChat — the agentic ("Ask") panel.
 *
 * Renders the incrementally-streamed answer in an aria-live log region and the
 * validated citations beneath it as clickable links. The actual streaming is
 * driven by {@link useAgentStream} in the page; this component is presentational.
 */
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Citation } from "./Citation";
import type { AgentCitation } from "@records-monitor/shared";
import type { ReactElement, ReactNode } from "react";

// Matches plain http(s) URLs in flowing text. Stops at whitespace and at most
// common sentence-end punctuation so trailing periods don't get swallowed into
// the href. Conservative on purpose — URLs inside the answer are an unusual
// case (the model is supposed to cite via tool calls); this is a defensive
// fallback so embedded URLs are clickable.
const URL_RE = /(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]])/g;

/**
 * Split ``text`` into an alternating list of strings and clickable
 * external <a> links for any URLs found. Renders inline; safe for
 * arbitrary user content because every chunk goes through React's text
 * escaping (no dangerouslySetInnerHTML).
 */
function linkifyAnswerText(text: string): ReactNode[] {
  if (!text) return [text];
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // Use exec in a loop so we have positional information for slicing.
  const re = new RegExp(URL_RE);
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const url = match[0];
    parts.push(
      <Link key={`${match.index}-${url}`} href={url} target="_blank" rel="noreferrer noopener">
        {url}
      </Link>,
    );
    lastIndex = match.index + url.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

export interface AgentChatProps {
  question: string;
  answer: string;
  citations: readonly AgentCitation[];
  isStreaming: boolean;
  isDone: boolean;
  errorMessage: string | null;
  onOpenCitation: (citation: AgentCitation) => void;
}

export function AgentChat({
  question,
  answer,
  citations,
  isStreaming,
  isDone,
  errorMessage,
  onOpenCitation,
}: AgentChatProps): ReactElement {
  const hasStarted = isStreaming || isDone || answer.length > 0;

  return (
    <Stack spacing={2} aria-label="Agent chat" component="section">
      {question ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Your question
          </Typography>
          <Typography>{question}</Typography>
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Answer
        </Typography>
        <Typography
          component="div"
          role="log"
          aria-label="Agent answer"
          aria-live="polite"
          aria-busy={isStreaming}
          sx={{ whiteSpace: "pre-wrap", minHeight: 24 }}
        >
          {linkifyAnswerText(answer)}
          {isStreaming ? (
            <CircularProgress
              size={14}
              aria-label="Generating answer"
              sx={{ ml: 1, verticalAlign: "middle" }}
            />
          ) : null}
          {!hasStarted ? (
            <Typography component="span" color="text.secondary">
              Ask a question to get an answer with citations.
            </Typography>
          ) : null}
        </Typography>
      </Paper>

      {errorMessage ? (
        <Typography role="alert" color="error">
          {errorMessage}
        </Typography>
      ) : null}

      {citations.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" component="h3">
            Sources
          </Typography>
          <List dense aria-label="Citations">
            {citations.map((citation) => (
              <Citation key={citation.marker} citation={citation} onOpen={onOpenCitation} />
            ))}
          </List>
        </Paper>
      ) : null}
    </Stack>
  );
}
